import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';

import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';

import usePersistantState from 'hooks/state.hook';

import { SITE_COLUMN } from 'constants/import.constants';

import { Button, Client, CommonGlobalService, CommonUtilsService, DownloadIcon, RootState, Site, SiteFilter, UploadIcon, UUID } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { SiteComponent } from '../components/sites/site.component';
import { SearchInput } from '../components/inputs/search_input.component';
import { Loading } from '../components/loading.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';
import { SeeMore } from '../components/inputs/see_more.component';

import { SitesIcon } from '../components/icons/sites_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';
import { ExportModal } from 'features/import_export/components/modal_export.component';
import { ImportModal } from 'features/import_export/components/modal_import.component';
import { ImportHelperModal } from 'features/import_export/components/import_helper_modal.component';


export function SitesView() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  /*----------------------------- useState ------------------------------*/
  const [ clients, setClients ] = useState<Client[]>( [] );
  const [ sites, setSites ] = useState<Site[]>( [] );

  const [ filterName, setFilterName ] = usePersistantState<string>( 'site_filterName', '' );
  const [ filterClients, setFilterClients ] = usePersistantState<UUID[]>( 'site_filterClients', [] );

  const [ seeMoreClients, setSeeMoreClients ] = useState<boolean>( false );
  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ isFirstRender, setIsFirstRender ] = useState<boolean>( true );
  const [ isFilterEnd, setIsFilterEnd ] = useState<boolean>( false );

  const [ isOpenExportModal, setIsOpenExportModal ] = useState<boolean>( false );
  const [ isOpenImportModal, setIsOpenImportModal ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    refreshFilters();
  }, [] );

  useEffect( () => {
    if ( isFilterEnd ) {
      checkClientFilter();
      setTimeout( () => { setIsFirstRender( false ); }, 75 );
    }
  }, [ isFilterEnd ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      refreshSites();
    }
  }, [ isFirstRender, filterName, filterClients ] );

  useEffect( () => {
    if ( !isFirstRender ) {
      setSeeMoreClients( filterClients.length > 0 );
    }
  }, [ isFirstRender, filterClients ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshFilters = async (): Promise<void> => {
    const responseClients = await ClientService.getAllowedClients( null, token, dispatch ) ?? [];
    CommonUtilsService.sortByKey( responseClients, 'name', 'asc' );
    setClients( responseClients );
    setIsFilterEnd( true );
  };

  const refreshSites = async (): Promise<void> => {
    setIsLoading( true );
    const filters: SiteFilter = {
      filterName: filterName && filterName != '' ? filterName : undefined,
      filterClients: filterClients && filterClients.length > 0 ? filterClients : undefined,
    };
    const responseSites = await SiteService.getAllowedSites( filters, token, dispatch ) ?? [];
    responseSites.sort( ( site1, site2 ) => {
      const clientName1 = clients.find( client => { return client.id === site1.clientId; } )?.name ?? '';
      const clientName2 = clients.find( client => { return client.id === site2.clientId; } )?.name ?? '';
      if ( clientName1 < clientName2 ) { return -1; }
      if ( clientName1 > clientName2 ) { return 1; }
      if ( site1.name < site2.name ) { return -1; }
      if ( site1.name > site2.name ) { return 1; }
      return 0;
    } );
    setSites( responseSites );
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const checkClientFilter = (): void => {
    const newFilter = filterClients
      .filter( clientIdFilter => {
        return clients.find( client => { return client.id === clientIdFilter; } ) != null;
      } );
    if ( newFilter.length != filterClients.length ) {
      setFilterClients( newFilter );
    }
  };

  /*------------------------------ Events -------------------------------*/
  const onChangeFilteredClients = ( clientId: UUID, value: boolean ) => {
    if ( value ) {
      setFilterClients( [ ...filterClients, clientId ] );
    } else {
      const copyFilterClients = [ ...filterClients ];
      const index = copyFilterClients.findIndex( item => { return item === clientId; } );
      if ( index != -1 ) {
        copyFilterClients.splice( index, 1 );
      }
      setFilterClients( copyFilterClients );
    }
  };

  const openExportModal = () => {
    setIsOpenExportModal( true );
  };
  const closeExportModal = () => {
    setIsOpenExportModal( false );
  };

  const openImportModal = () => {
    setIsOpenImportModal( true );
  };
  const closeImportModal = ( isImported?: boolean ) => {
    setIsOpenImportModal( false );
    if ( isImported === true ) {
      refreshSites();
    }
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div>
        <div>
          <HomeTopBar title={ t( 'Site' ) } needConfirmationToNav={ false }/>
        </div>
        <div className='flex h-full gap-1 mx-2'>

          <div className='flex flex-col gap-10 pt-10 w-[250px] h-full'>
            {
              me && CommonGlobalService.isClientAdmin( me ) ?
                <div className='flex flex-col w-[250px]'>
                  <Button
                    label={ t( 'New site' ) }
                    className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                    icon={ <PlusIcon className='w-[24px] h-[24px] p-0.5 fill-white' /> }
                    onClick={ () => { NavigationService.toNewSite( navigate, false ); } }
                  />
                  <div className='flex'>
                    <div className='flex items-center w-full'>
                      <Button
                        label={ t( 'common:export' ) }
                        className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                        icon={ <DownloadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                        onClick={ openExportModal }
                      />
                    </div>
                    <div className='flex items-center'>
                      <Button
                        label={ t( 'common:import' ) }
                        className='w-full m-1 bg-[#0BB173] hover:bg-[#088D5C] hover:ring-1 hover:ring-[#088D5C] text-sm font-medium text-white'
                        icon={ <UploadIcon className='w-[24px] h-[24px] p-0.5 text-white'/> }
                        onClick={ openImportModal }
                      />
                      <ImportHelperModal
                        title={ t( 'import_help_title' ) }
                        text={
                          <div className='space-y-2 text-sm text-gray-700'>
                            <p className='font-semibold text-gray-800'>{ t( 'template_columns_intro' ) }</p>
                            <ul className='list-disc pl-5 space-y-1'>
                              {
                                Object.values( SITE_COLUMN )
                                  .map( column => {
                                    return ( <li key={ column }> { t( column ).trim() } </li> );
                                  } )
                              }
                            </ul>
                          </div>
                        }
                        api={ () => {return SiteService.downloadSitesTemplate( token, dispatch );} }
                      />
                    </div>
                  </div>
                </div>
                :
                null
            }
            {
              clients.length != 0 ?
                <div className='relative border p-1 rounded-lg text-left h-fit w-full'>
                  <div className='absolute w-fit px-1.5 -top-3.5 left-3 bg-white font-bold'>{ t( 'Client' ) }</div>
                  <div className='px-4 py-2 text-sm'>
                    {
                      clients.slice( 0, seeMoreClients ? clients.length : 5 ).map( client => {
                        return (
                          <div
                            key={ client.id }
                            className='flex flex-col mb-1'
                          >
                            <div className='flex gap-2 items-center select-none'>
                              <input
                                type='checkbox'
                                id={ client.id }
                                className='h-fit cursor-pointer'
                                checked={ filterClients.find( clientId => { return clientId === client.id; } ) != null }
                                onChange={ e => { onChangeFilteredClients( client.id, ( e.target as HTMLInputElement ).checked ); } }
                              />
                              <label htmlFor={ client.id } className='font-medium cursor-pointer truncate'>{ client.name }</label>
                            </div>
                          </div>
                        );
                      } )
                    }
                    {
                      clients.length > 5 ?
                        <SeeMore
                          isSeeMore={ seeMoreClients }
                          onClick={ () => { setSeeMoreClients( !seeMoreClients ); } }
                          t={t}
                        />
                        :
                        null
                    }
                  </div>
                </div>
                :
                null
            }
          </div>

          <div className='flex flex-col w-full h-full mx-2 items-center'>
            {
              isFilterEnd ?
                <div className='flex w-1/2 min-w-[400px] gap-2 justify-center items-center'>
                  <SearchInput
                    value={ filterName }
                    onSearched={ value => { setFilterName( value ); } }
                    t={ t }
                  />
                </div>
                :
                null
            }
            {
              !isLoading ?
                <div className='flex flex-col gap-10 justify-center w-full h-full'>
                  {
                    sites.length > 0 ?
                      <div className='flex flex-wrap w-full justify-center'>
                        {
                          sites.map( site => {
                            const client = clients.find( item => { return item.id === site.clientId; } );
                            return (
                              <SiteComponent
                                key={ site.id }
                                clientOfSite={ client }
                                site={ site }
                                t={ t }
                              />
                            );
                          } )
                        }
                      </div>
                      :
                      <div className='flex flex-col gap-4 w-full h-full pt-10 items-center justify-center'>
                        <SitesIcon className='h-24'/>
                        <div className='flex flex-col items-center justify-center'>
                          <span className='font-bold'>{t( 'common:oops' ) }</span>
                          <span className='font-medium'>{ t( 'No sites found !' ) }</span>
                        </div>
                      </div>
                  }

                  <ExportModal
                    title={ t( 'translation:export_sites' ) }
                    isOpen={ isOpenExportModal }
                    onClose={ closeExportModal }
                    exportFunction={ SiteService.exportSitesOfClient }
                    showSiteFilter={ false }
                    showSectorFilter={ false }
                  />
                  <ImportModal
                    title={ t( 'translation:import_sites' ) }
                    isOpen={ isOpenImportModal }
                    onClose={ closeImportModal }
                    importFunction={ SiteService.importSitesOnClient}
                  />
                </div>
                :
                <Loading/>
            }

          </div>
        </div>
      </div>
    </>
  );

}